import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiPlus } from "react-icons/fi";
import { BiCommentDetail } from "react-icons/bi";
import { Overlay, Popover } from "react-bootstrap";
import { toastr } from "react-redux-toastr";

import DurationInput from "components/TimesBalance/DurationInput";
import { transactionsService } from "services";
import { errorFormating } from "utils/utils";
import {
	industrialMinutesToHours,
	isValidDurationMinutes,
} from "utils/timeBalance";

const ManualEntryCreateCell = ({
	week,
	employeeId,
	accountId,
	isEditing,
	onStartEdit,
	onFinishEdit,
	onSaved,
}) => {
	const { t } = useTranslation();
	const [minutes, setMinutes] = useState(0);
	const [operation, setOperation] = useState("add");
	const [observations, setObservations] = useState("");
	const [draftObservations, setDraftObservations] = useState("");
	const [showObservations, setShowObservations] = useState(false);
	const formRef = useRef(null);
	const commentBtnRef = useRef(null);
	const savingRef = useRef(false);

	const resetForm = () => {
		setMinutes(0);
		setOperation("add");
		setObservations("");
		setDraftObservations("");
		setShowObservations(false);
		savingRef.current = false;
	};

	useEffect(() => {
		if (!isEditing) {
			resetForm();
			return;
		}

		requestAnimationFrame(() => {
			const input = formRef.current?.querySelector("input");
			input?.focus();
		});
	}, [isEditing]);

	const saveEntry = async () => {
		if (savingRef.current || showObservations) return;

		if (!isValidDurationMinutes(minutes)) {
			onFinishEdit();
			return;
		}

		savingRef.current = true;

		const day = week.end.format("YYYY-MM-DD");
		const payload = {
			employee_id: employeeId,
			account_id: accountId,
			day,
			value: industrialMinutesToHours(minutes, operation),
			observations,
		};

		const response = await transactionsService.add(payload);

		if (response.ok) {
			toastr.success(t("common.success"), t("common.changesSaved"));
			onSaved();
		} else {
			const errors = errorFormating(response);
			const message =
				errors?.general || errors?.value || t("common.errorGeneric");
			toastr.error(t("common.error"), message);
		}

		savingRef.current = false;
		onFinishEdit();
	};

	const isInsideForm = (node) => {
		if (!node) return false;
		return (
			formRef.current?.contains(node) ||
			node.closest?.(".tb-observations-popover")
		);
	};

	const handleBlur = (event) => {
		if (savingRef.current || showObservations) return;

		const nextTarget = event.relatedTarget;
		if (isInsideForm(nextTarget)) return;

		saveEntry();
	};

	const handleKeyDown = (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			saveEntry();
			return;
		}

		if (event.key === "Tab") {
			saveEntry();
		}

		if (event.key === "Escape") {
			event.preventDefault();
			if (showObservations) {
				setDraftObservations(observations);
				setShowObservations(false);
				return;
			}
			onFinishEdit();
		}
	};

	const openObservations = () => {
		setDraftObservations(observations);
		setShowObservations(true);
	};

	const saveObservations = () => {
		setObservations(draftObservations);
		setShowObservations(false);
		requestAnimationFrame(() => {
			formRef.current?.querySelector("input")?.focus();
		});
	};

	const toggleOperation = () => {
		setOperation((prev) => (prev === "add" ? "subtract" : "add"));
	};

	const keepInputFocus = (event) => {
		event.preventDefault();
	};

	const hasObservations = Boolean(observations.trim());

	if (!isEditing) {
		return (
			<button
				type="button"
				className="tb-manual-add-btn"
				onClick={onStartEdit}
				disabled={savingRef.current || !accountId}
				aria-label={t("times.balance.createManualEntry")}
			>
				<FiPlus />
			</button>
		);
	}

	return (
		<div ref={formRef} className="tb-manual-create-form" onBlur={handleBlur}>
			<div className="tb-manual-create-form__input-row">
				<button
					type="button"
					className={`tb-manual-create-form__sign tb-manual-create-form__sign--${operation}`}
					onMouseDown={keepInputFocus}
					onClick={toggleOperation}
					aria-label={
						operation === "add"
							? t("times.balance.manualEntry.addTime")
							: t("times.balance.manualEntry.subtractTime")
					}
				>
					{operation === "add" ? "+" : "−"}
				</button>
				<DurationInput
					minutes={minutes}
					onChange={setMinutes}
					onKeyDown={handleKeyDown}
					className="tb-manual-create-form__field"
					autoFocus
				/>
				<button
					ref={commentBtnRef}
					type="button"
					className={`tb-manual-create-form__comment-btn${
						hasObservations ? " tb-manual-create-form__comment-btn--active" : ""
					}${showObservations ? " tb-manual-create-form__comment-btn--open" : ""}`}
					onClick={openObservations}
					aria-label={t("transactions.form.observations")}
					aria-expanded={showObservations}
				>
					<BiCommentDetail />
				</button>
			</div>

			<Overlay
				show={showObservations}
				target={commentBtnRef.current}
				placement="bottom"
				rootClose
				onHide={() => {
					setDraftObservations(observations);
					setShowObservations(false);
				}}
			>
				<Popover
					id={`tb-observations-popover-${week.weekNumber}`}
					className="tb-observations-popover"
				>
					<Popover.Header as="div" className="tb-observations-popover__header">
						{t("transactions.form.observations")}
					</Popover.Header>
					<Popover.Body className="tb-observations-popover__body">
						<textarea
							className="form-control tb-observations-popover__textarea"
							value={draftObservations}
							onChange={(event) => setDraftObservations(event.target.value)}
							placeholder={t("transactions.form.observations")}
							rows={3}
							autoFocus
						/>
						<div className="tb-observations-popover__footer">
							<button
								type="button"
								className="btn btn-primary btn-sm"
								onClick={saveObservations}
							>
								{t("common.save")}
							</button>
						</div>
					</Popover.Body>
				</Popover>
			</Overlay>
		</div>
	);
};

export default ManualEntryCreateCell;
